import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  Alert,
  Divider,
  Form,
  Select,
  Dropdown,
  Space,
  Flex,
  Row,
  Col,
  Spin,
  Typography,
  Tooltip,
  Input,
} from "antd";
import {
  LoadingOutlined,
  PlusOutlined,
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
  EditFilled,
  DeleteFilled,
  FileExcelFilled,
  CaretDownFilled,
  FileFilled,
  SearchOutlined,
} from "@ant-design/icons";
import { defaultTruckCompanyDetail } from "../../constance/dataTypes";
import {
  defaultWoodSizeBuyPriceMapDetail,
  WOOD_IMPORT_GRADE_DISPLAY,
} from "../../constance/dataTypes/barcode";
import { URL_BARCODE, URL_SCALE_LARGE } from "../../constance/urls";
import useItemDetail from "../../hooks/useItemDetail";
import useSearchItem from "../../hooks/useSeatchItem";
import { POST } from "../../utils/HttpClient";
import { ellipsisString } from "../../utils/stringFormat";
import { Toast } from "../../utils/Toast";
import ConfirmModal from "../common/ConfirmModal";
import TSTable from "../common/TSTable";
import WoodImportSettingsPriceModal from "./WoodImportSettingsPriceModal";
import * as _ from "lodash";
import { BLUE_COLOR, GREEN, RED_COLOR } from "../../constance/color";
import { useSearchTable } from "../../hooks/useSearchTable";
import ColorButton from "../common/ColorButton";
import { useDebounce } from "use-debounce";

export default function WoodImportSettingPriceList() {
  const { t } = useTranslation();
  const tableRef = React.useRef();
  const fileInputRef = React.useRef();
  const [hasSearched, setHasSearched] = React.useState(false);

  const columns = React.useMemo(
    () => [
      {
        title: "",
        dataIndex: "info",
        children: [
          {
            title: t("wood_imports.wood_code"),
            dataIndex: "wood_code",
          },
          {
            title: t("wood_imports.truck_company"),
            dataIndex: "truck_company_name",
            render: (_, original) => (
              <Tooltip title={ellipsisString(original.truck_company_name, 30)}>
                <label>{ellipsisString(original.truck_company_name, 30)}</label>{" "}
              </Tooltip>
            ),
          },
          {
            title: t("wood_imports.vat"),
            dataIndex: "vat",
            render: (_, original) => (
              <div style={{ textAlign: "center" }}>
                {original.vat ? (
                  <CheckOutlined style={{ color: GREEN }} />
                ) : (
                  <CloseOutlined style={{ color: RED_COLOR }} />
                )}
              </div>
            ),
          },
        ],
      },
      {
        title: () => (
          <div style={{ textAlign: "center" }}>
            {t("wood_imports.buy_price")}
          </div>
        ),
        dataIndex: "buy_price",

        children: [
          ...WOOD_IMPORT_GRADE_DISPLAY.map((grade) => {
            return {
              title: t(`wood_imports.grades.${grade}`),
              dataIndex: `buy_${grade}`,
            };
          }),
        ],
      },
      {
        title: () => (
          <div style={{ textAlign: "center" }}>
            {t("wood_imports.sell_price")}
          </div>
        ),
        dataIndex: "sell_price",
        children: [
          ...WOOD_IMPORT_GRADE_DISPLAY.map((grade) => {
            return {
              title: t(`wood_imports.grades.${grade}`),
              dataIndex: `sell_${grade}`,
            };
          }),
        ],
      },

      {
        title: "",
        dataIndex: "id",
        render: (_, original) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Flex gap="middle">
              <ColorButton
                variant="outlined"
                color="primary"
                override={BLUE_COLOR}
                content={t("edit")}
                icon={<EditFilled />}
                onClick={() => {
                  setIsDetailModalOpen(true);
                  setDetail(original);
                }}
              >
                {t("edit")}
              </ColorButton>
              <ColorButton
                negative
                color="danger"
                variant="outlined"
                basic
                icon={<DeleteFilled />}
                onClick={() => {
                  setIsDeleteConFirmModalOpen(true);
                  setDetail(original);
                }}
              ></ColorButton>
            </Flex>
          </div>
        ),
      },
    ],
    []
  );

  const MIN_SEARCH_LENGTH = 2;
  const [form] = Form.useForm();
  const isVat = Form.useWatch("vat", form);
  const searchValue = Form.useWatch("truckCompanyName", form);
  const [debouncedText] = useDebounce(searchValue, 500);

  const [truckCompany, setTruckCompany] = React.useState(
    defaultTruckCompanyDetail
  );
  const [detail, setDetail] = React.useState(defaultWoodSizeBuyPriceMapDetail);

  // import export state
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(false);

  // detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
  const [isDeleteConFirmModalOpen, setIsDeleteConFirmModalOpen] =
    React.useState(false);

  const truckCompanies = useSearchItem({
    url: URL_SCALE_LARGE.TRUCK_COMPANY,
    description_render: "name",
    title_render: "code",
  });

  const handleImportFile = async (file) => {
    setIsLoading(true);
    setErrorMessages(null);
    try {
      await POST(
        URL_BARCODE.WOOD_SIZE_BUY_PRICE_MAP_IMPORT,
        { attachment: file },
        true
      );
      Toast.success(`${t("save")} ${t("success")}`);
      if (tableRef.current && truckCompany) {
        tableRef.current.fetch();
      }
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const woodSizeBuyPriceMapDetail = useItemDetail({
    url: URL_BARCODE.WOOD_SIZE_BUY_PRICE_MAP,
    id: detail.id ? detail.id : null,
    manual: true,
    onSuccess: () => {
      Toast.success(`${t("save")} ${t("success")}`);
      setIsDetailModalOpen(false);
      setDetail(defaultWoodSizeBuyPriceMapDetail);
      if (tableRef.current && truckCompany) {
        tableRef.current.fetch();
      }
    },
    onError: (error) => {
      Toast.error(`${t("save")} ${t("error")}: ${error.message}`);
    },
  });

  const exportMenu = (
    <Dropdown
      menu={{
        items: [
          {
            key: "excel",
            icon: <FileExcelFilled style={{ color: "green" }} />,
            label: "Excel",
            onClick: () =>
              window.open(
                `${URL_BARCODE.WOOD_SIZE_BUY_PRICE_MAP_EXPORT}excel/?vat=${isVat}&truck_company_code=${truckCompany.code}&charset=utf-8`
              ),
          },
          {
            key: "csv",
            icon: <FileFilled style={{ color: BLUE_COLOR }} />,
            label: "CSV",
            onClick: () =>
              window.open(
                `${
                  URL_BARCODE.WOOD_SIZE_BUY_PRICE_MAP_EXPORT
                }csv/?vat=${isVat}&truck_company_code=${
                  truckCompany?.code || ""
                }&charset=utf-8`
              ),
          },
        ],
      }}
      placement="bottomLeft"
      arrow
    >
      <ColorButton variant="outlined" color="primary" override={GREEN}>
        {t("export")} <CaretDownFilled />
      </ColorButton>
    </Dropdown>
  );

  React.useEffect(() => {
    if (debouncedText && debouncedText.length >= MIN_SEARCH_LENGTH) {
      truckCompanies.fetchData({ search: debouncedText });
    }
  }, [debouncedText]);

  React.useEffect(() => {
    if (!hasSearched) {
      setHasSearched(true);
      return;
    } else {
      tableRef.current.fetch();
    }
  }, [truckCompany, isVat]);

  return (
    <div>
      <ConfirmModal
        open={isDeleteConFirmModalOpen}
        onClose={() => setIsDeleteConFirmModalOpen(false)}
        title={t("confirms.delete_title")}
        content={t("confirms.delete_content")}
        onConfirm={async () => {
          setIsDeleteConFirmModalOpen(false);
          try {
            await woodSizeBuyPriceMapDetail.handleDelete(detail.id);
            if (tableRef.current) {
              tableRef.current.fetch();
            }
          } catch (error) {
            Toast.error(`${t("delete")} ${t("error")}: ${error.message}`);
          }
        }}
      />
      <WoodImportSettingsPriceModal
        open={isDetailModalOpen}
        detail={detail}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetail(defaultWoodSizeBuyPriceMapDetail);
        }}
        woodSizeBuyPriceMapDetail={woodSizeBuyPriceMapDetail}
        onSuccess={() => {
          setIsDetailModalOpen(false);
          setDetail(defaultWoodSizeBuyPriceMapDetail);
        }}
      />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Space>
          <ColorButton
            type="primary"
            override={BLUE_COLOR}
            icon={<DownloadOutlined />}
            loading={isLoading}
            content={t("import")}
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            {t("import")}
          </ColorButton>

          <ColorButton
            {...(true && { className: "positive" })}
            type="primary"
            override={GREEN}
            icon={<PlusOutlined />}
            content={t("wood_imports.add_code")}
            onClick={() => setIsDetailModalOpen(true)}
          >
            {t("wood_imports.add_code")}
          </ColorButton>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.value = null;
              }
            }}
            onChange={(e) => {
              handleImportFile(e.target.files[0]);
            }}
          />
        </Space>
      </div>

      <Row justify="space-between" align="middle">
        <Col style={{ display: "flex", justifyContent: "flex-start" }}>
          <Form form={form} initialValues={{ vat: "all" }}>
            <Row gutter={16}>
              <Space>
                <Col>
                  <Form.Item
                    label={t("wood_imports.truck_company")}
                    name="truckCompanyName"
                  >
                    <AutoComplete
                      style={{ width: 200 }}
                      notFoundContent={searchValue ? t("no_data") : ""}
                      placeholder={`${t("search")} ${t(
                        "searchs.fields.code"
                      )} ${t("or")} ${t("searchs.fields.name")}...`}
                      onSearch={(value) => {
                        setTruckCompany(defaultTruckCompanyDetail);
                      }}
                      onSelect={(value, option) => {
                        setTruckCompany({
                          id: option.value,
                          code: option.value,
                          name: option.label,
                        });
                        form.setFieldsValue({
                          truckCompanyName: `${option.name}`,
                        });
                      }}
                      options={truckCompanies.response.map((company) => ({
                        value: company.code,
                        name: company.name,
                        label: (
                          <div>
                            <div>
                              <strong>{company.code}</strong>
                            </div>
                            <Typography.Text type="secondary">
                              {company.name}
                            </Typography.Text>
                          </div>
                        ),
                      }))}
                      className="custom-autocomplete"
                    >
                      <Input
                        suffix={
                          truckCompanies.isLoading ? (
                            <Spin
                              indicator={<LoadingOutlined spin />}
                              color="default"
                            />
                          ) : (
                            <SearchOutlined />
                          )
                        }
                        allowClear
                        disabled={truckCompanies.isLoading}
                      ></Input>
                    </AutoComplete>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label={t("wood_imports.vat")} name="vat">
                    <Select
                      style={{ width: 200 }}
                      options={[
                        { value: "all", label: t("all") },
                        { value: true, label: t("wood_imports.vat") },
                        { value: false, label: t("wood_imports.no_vat") },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Space>
            </Row>
          </Form>
        </Col>

        <Col style={{ display: "flex", justifyContent: "flex-end" }}>
          {exportMenu}
        </Col>
      </Row>

      <Divider hidden />
      {errorMessages && (
        <Alert
          message={t("error")}
          description={errorMessages}
          type="error"
          showIcon
          closable
        />
      )}

      <TSTable
        scroll={{ x: 1000 }}
        ref={tableRef}
        url={URL_BARCODE.WOOD_SIZE_BUY_PRICE_MAP}
        columns={columns}
        params={{
          vat: isVat === "all" ? null : isVat,
          truck_company_code: truckCompany.code,
        }}
      />
    </div>
  );
}
